import { createFileRoute } from "@tanstack/react-router";
import { SeoPage } from "@/components/SeoPage";
import { faqJsonLd } from "@/lib/seo-jsonld";
import { breadcrumbJsonLd } from "@/lib/seo-jsonld";
import { KOMMUN_LINKS, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN, LAST_REVIEWED } from "@/lib/official-sources";

const faqs = [
  { q: "Vart ansöker jag om serveringstillstånd i Västerås?", a: "Ansökan görs till Individ- och familjenämnden / tillståndsenheten i Västerås stad. Verksamhet i annan kommun i Västmanlands län (t.ex. Hallstahammar, Surahammar, Enköping, Köping) söker hos respektive kommun." },
  { q: "Vad kostar serveringstillstånd i Västerås?", a: "Västerås kommun tar ut en ansökningsavgift och en årlig tillsynsavgift. Avgifterna varierar beroende på tillståndstyp och alkoholförsäljningens storlek – kontrollera aktuella avgifter på kommunens webbplats." },
  { q: "Hur lång är handläggningstiden i Västerås?", a: "Västerås kommun anger normalt 8–12 veckor för en komplett ansökan om stadigvarande tillstånd. Tillfälliga tillstånd kan gå snabbare." },
];

export const Route = createFileRoute("/serveringstillstand-vasteras")({
  component: () => (
    <SeoPage
      breadcrumbs={[
        { label: "Hem", to: "/" },
        { label: "Guider", to: "/serveringstillstand-och-kunskapsprov" },
        { label: "Västerås" },
      ]}
      title="Serveringstillstånd Västerås – ansökan och krav"
      intro="Allt du behöver veta om serveringstillstånd i Västerås: var du ansöker, vilka krav som ställs och hur du förbereder dig för kunskapsprovet."
      lastReviewed={LAST_REVIEWED}
      sources={[KOMMUN_LINKS.vasteras, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN]}
      localData={{
        unit: "Västerås stad – Miljö- och hälsoskyddsförvaltningen",
        address: "Stadshuset, 721 87 Västerås",
        phone: "021-39 00 00 (Västerås stads kontaktcenter, knappval 9)",
        waitTime: "ca 8–12 veckor för stadigvarande tillstånd; vissa ärenden som konkursförvaltning och cateringanmälan kan gå betydligt snabbare",
        feeExamples: [
          "Ansöknings- och tillsynsavgifter enligt fastställd taxa – se kommunens sida för aktuella belopp",
          "Längre handläggningstider kan förekomma under juni–augusti på grund av semestrar",
        ],
        checklist: [
          "Registreringsbevis",
          "Finansieringsplan och kontoutdrag",
          "Hyresavtal eller nyttjanderätt till lokalen",
          "Ritning över serveringsyta",
          "Brandskyddsdokumentation",
          "PBI-uppgifter och godkänt kunskapsprov",
        ],
        officialUrl: KOMMUN_LINKS.vasteras,
      }}
      sections={[
        { h2: "Var ansöker du i Västerås?", body: "Individ- och familjenämnden / tillståndsenheten i Västerås stad ansvarar för serveringstillstånd i Västerås kommun. För verksamhet i kringliggande kommuner som Hallstahammar, Surahammar, Enköping, Köping ansöker du hos respektive kommun. Varje kommun har egna blanketter och e-tjänster, men kraven enligt alkohollagen är nationella och gäller överallt." },
        { h2: "Krav från Västerås kommun", body: "Utöver de nationella kraven (personlig och ekonomisk lämplighet, godkänt kunskapsprov och lämplig lokal) kan kommunen ha lokala riktlinjer kring serveringstider, uteservering och ordning. Från 1 juni 2026 är kraven på eget kök och varierat matutbud borttagna nationellt. Västerås har ett aktivt hamn- och evenemangsområde där tillfälliga tillstånd är vanliga under sommaren." },
        { h2: "Kunskapsprovet i Västerås", body: "Provet genomförs hos kommunen efter att ansökan har kommit in. Det är ett skriftligt prov inom alkohollagen, serveringsbestämmelser, ordning och nykterhet, marknadsföring m.m. Frågeantalet varierar beroende på tillståndstyp och kommun – Folkhälsomyndighetens föreskrifter FoHMFS 2014:7 anger områden och miniminivåer. Med Alkoholprovet kan du träna i samma format hemifrån och gå in på provet förberedd." },
        { h2: "Handläggningstid i Västerås", body: "Västerås stad nås via kontaktcenter på 021-39 00 00 (knappval 9) eller serveringstillstand@vasteras.se. Räkna med ca 8–12 veckor för ett stadigvarande tillstånd, medan enklare ärenden som anmälan om catering eller ändringar vid konkursförvaltning kan hanteras på 1–2 veckor. Kommunen flaggar själv för längre handläggningstider under juni–augusti på grund av semestrar, så planera ansökan i god tid inför en sommaröppning." },
        { h2: "Tillsyn och uppföljning", body: "Västerås kommun bedriver aktiv tillsyn på serveringsställen tillsammans med polis och Skatteverket. Anmärkningar kan leda till erinran, varning, sanktionsavgift eller återkallat tillstånd. Goda kunskaper i alkohollagen minskar risken för misstag i den dagliga driften." },
      ]}
      faqs={faqs}
      related={{
        title: "Relaterade sidor",
        links: [
          { to: "/serveringstillstand-och-kunskapsprov", label: "Hub – alla guider" },
          { to: "/serveringstillstand-stockholm", label: "Serveringstillstånd Stockholm" },
          { to: "/serveringstillstand-goteborg", label: "Serveringstillstånd Göteborg" },
          { to: "/serveringstillstand-malmo", label: "Serveringstillstånd Malmö" },
          { to: "/serveringstillstand-restaurang", label: "För restaurang" },
          { to: "/serveringstillstand-bar", label: "För bar & nattklubb" },
          { to: "/tillfalligt-serveringstillstand", label: "Tillfälligt tillstånd" },
          { to: "/kunskapsprov-alkohollagen", label: "Kunskapsprov i alkohollagen" },
          { to: "/foretag", label: "Företagslösning" },
        ],
      }}
    />
  ),
  head: () => ({
    meta: [
      { title: "Serveringstillstånd Västerås – krav, prov & tips" },
      { name: "description", content: "Så får du serveringstillstånd i Västerås: lokala krav, tillsynsavgifter och hur du klarar kunskapsprovet." },
      { property: "og:title", content: "Serveringstillstånd Västerås – tips" },
      { property: "og:description", content: "Ansökan, krav, avgifter och kunskapsprov för serveringstillstånd i Västerås." },
      { property: "og:url", content: "https://alkoholprovet.se/serveringstillstand-vasteras" },
    ],
    links: [{ rel: "canonical", href: "https://alkoholprovet.se/serveringstillstand-vasteras" }],
    scripts: [
      faqJsonLd(faqs),
      breadcrumbJsonLd([
        { label: "Hem", url: "https://alkoholprovet.se/" },
        { label: "Guider", url: "https://alkoholprovet.se/serveringstillstand-och-kunskapsprov" },
        { label: "Västerås", url: "https://alkoholprovet.se/serveringstillstand-vasteras" },
      ]),
    ],
  }),
});
